import { useState, useMemo } from 'react';
import { FileText, Search, Check, CheckSquare, Square } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useSources } from '@/features/sources/hooks/useSources';
import { useFolders } from '@/features/sources/hooks/useFolders';

interface SourceSelectorProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function SourceSelector({
  selectedIds,
  onSelectionChange,
}: SourceSelectorProps) {
  const [search, setSearch] = useState('');
  const [folderId, setFolderId] = useState<string>('all');
  const { data: sources = [], isLoading: sourcesLoading } = useSources(
    folderId !== 'all' ? { folder_id: folderId } : {}
  );
  const { data: folders = [], isLoading: foldersLoading } = useFolders();

  // Filter sources: only those with extracted text
  const readySources = useMemo(() => {
    return sources.filter((s) => s.extraction_status === 'completed' && s.extracted_text);
  }, [sources]);

  // Apply search and folder filters
  const filteredSources = useMemo(() => {
    let result = readySources;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.file_name.toLowerCase().includes(q) ||
          (s.title && s.title.toLowerCase().includes(q)) ||
          (s.authors && s.authors.some((a: string) => a.toLowerCase().includes(q)))
      );
    }

    return result;
  }, [readySources, search]);

  const allSelected = filteredSources.length > 0 && filteredSources.every((s) => selectedIds.includes(s.id));

  function toggleSource(id: string) {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  }

  function toggleAll() {
    if (allSelected) {
      const filteredIds = new Set(filteredSources.map((s) => s.id));
      onSelectionChange(selectedIds.filter((id) => !filteredIds.has(id)));
    } else {
      const newIds = new Set([...selectedIds, ...filteredSources.map((s) => s.id)]);
      onSelectionChange([...newIds]);
    }
  }

  const isLoading = sourcesLoading || foldersLoading;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700">
          Select Sources ({selectedIds.length} selected)
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleAll}
          disabled={filteredSources.length === 0}
        >
          {allSelected ? (
            <>
              <CheckSquare className="mr-1 h-4 w-4" />
              Deselect All
            </>
          ) : (
            <>
              <Square className="mr-1 h-4 w-4" />
              Select All
            </>
          )}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search sources..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={folderId} onValueChange={setFolderId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All folders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All folders</SelectItem>
            {folders.map((folder) => (
              <SelectItem key={folder.id} value={folder.id}>
                {folder.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Source list */}
      <div className="max-h-[400px] overflow-y-auto rounded-lg border border-slate-200">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-slate-100" />
            ))}
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-slate-400">
            <FileText className="mb-2 h-8 w-8" />
            <p className="text-sm">
              {readySources.length === 0
                ? 'No sources with extracted text'
                : 'No sources match your filters'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredSources.map((source) => {
              const isSelected = selectedIds.includes(source.id);
              return (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => toggleSource(source.id)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50',
                    isSelected && 'bg-indigo-50 hover:bg-indigo-50'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                      isSelected
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-slate-300'
                    )}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5" />}
                  </div>

                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {source.title || source.file_name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {source.authors && source.authors.length > 0 && (
                        <span className="truncate">
                          {(source.authors as string[]).slice(0, 2).join(', ')}
                          {(source.authors as string[]).length > 2 && ' et al.'}
                        </span>
                      )}
                      {source.publication_year && (
                        <span>({source.publication_year})</span>
                      )}
                    </div>
                  </div>

                </button>
              );
            })}
          </div>
        )}
      </div>

      {readySources.length > 0 && (
        <p className="text-xs text-slate-400">
          {readySources.length} source{readySources.length !== 1 ? 's' : ''} ready for evaluation
        </p>
      )}
    </div>
  );
}
